import { tryOnScopeDispose } from '@vueuse/core'

/**
 * Lightweight wrapper around native `EventSource` for SSE connections.
 *
 * Reactively opens/closes the connection as `url` changes. Automatically
 * reconnects on drop (native browser behaviour) and cleans up on scope dispose.
 *
 * @param url - Reactive URL. Connection is closed when falsy.
 * @param onMessage - Called with raw `event.data` string on each unnamed message. Named events are not supported.
 * @returns `status` — current connection state; `error` — last error event.
 */
export function useSse(
  url: MaybeRefOrGetter<string | undefined>,
  onMessage: (data: string) => void,
) {
  const status = ref<'CONNECTING' | 'OPEN' | 'CLOSED'>('CLOSED')
  const error = ref<Event | null>(null)

  let eventSource: EventSource | null = null

  function close() {
    if (eventSource) {
      eventSource.close()
      eventSource = null
      status.value = 'CLOSED'
    }
  }

  watchEffect((onCleanup) => {
    const urlValue = toValue(url)

    if (!urlValue) {
      close()
      return
    }

    close()

    const es = new EventSource(urlValue)
    eventSource = es
    status.value = 'CONNECTING'

    es.onopen = () => {
      status.value = 'OPEN'
      error.value = null
    }

    es.onmessage = (event) => {
      onMessage(event.data)
    }

    es.onerror = (e) => {
      error.value = e
      status.value =
        es.readyState === EventSource.CONNECTING ? 'CONNECTING' : 'CLOSED'
    }

    onCleanup(() => {
      es.close()
      status.value = 'CLOSED'
    })
  })

  tryOnScopeDispose(close)

  return { status, error }
}
